/**
 * The rules a table's search box shares: one term, matched as a
 * case-insensitive substring against the text columns the row shows, as one
 * OR group the list ANDs with its other filters.
 *
 * For lists small enough that a plain LIKE scan is the right shape, with no
 * index and no prepared text; the styles lists, which need an index, keep
 * their own prepared column. The list search boxes build on this file so the
 * box behaves the same on every table; product mapping and the order hub
 * still carry their own versions until they are moved over. Backend code
 * imports it as `@retracedgmbh/modules/search-term`.
 *
 * The defaults are the shared rules; a list with a reason to differ (a list
 * of short codes, say) passes options rather than keeping its own copy.
 */

/**
 * Below this a term matches most of a list anyway. Letters and digits are
 * counted, not characters: `---` carries nothing to match on, and `a b` is
 * two letters, not three.
 */
export const SEARCH_MIN_LENGTH = 3;

const countSearchable = (term: string): number =>
  [...term].filter(ch => /[\p{L}\p{N}]/u.test(ch)).length;

export interface SearchTermOptions {
  /** Letters or digits a term needs; `SEARCH_MIN_LENGTH` unless given. */
  minLength?: number;
}

/**
 * The LIKE pattern for a search term, or `null` when the term is not a string
 * or too short to be worth a scan. Wildcards and the backslash are escaped so
 * the term matches literally (`ACME_EU` must not also find `ACME-EU`);
 * Postgres's default LIKE escape character is the backslash, so no ESCAPE
 * clause is needed.
 */
export const searchLikePattern = (
  raw: unknown,
  { minLength = SEARCH_MIN_LENGTH }: SearchTermOptions = {},
): string | null => {
  const term = typeof raw === 'string' ? raw.trim() : '';
  if (countSearchable(term) < minLength) return null;
  return `%${term.replace(/[\\%_]/g, '\\$&')}%`;
};

/**
 * `(UPPER(a) LIKE UPPER($n) OR UPPER(b) LIKE UPPER($n) …)` for one bound
 * pattern, folded on both sides so the case of the term and the column never
 * matters. A NULL column simply fails its branch. `extra` are complete
 * predicates appended to the group as they are, for a column the row shows
 * through a lookup (a label stored as an id) rather than as text.
 */
export const searchOrGroup = (
  exprs: string[],
  placeholder: string,
  extra: string[] = [],
): string =>
  `(${[
    ...exprs.map(expr => `UPPER(${expr}) LIKE UPPER(${placeholder})`),
    ...extra,
  ].join(' OR ')})`;

/**
 * The three binds a "best few matches" search needs, or `null` when
 * `searchLikePattern` would be: the trimmed term for an exact match, the
 * escaped `term%` for a starts-with match, and the escaped `%term%` the
 * search filters on.
 */
export interface SearchMatch {
  exact: string;
  prefix: string;
  contains: string;
}

export const searchMatch = (
  raw: unknown,
  options: SearchTermOptions = {},
): SearchMatch | null => {
  const contains = searchLikePattern(raw, options);
  if (!contains) return null;
  return {
    exact: (raw as string).trim(),
    prefix: contains.slice(1),
    contains,
  };
};

/**
 * 0 for an exact match on `expr`, 1 for a starts-with match, 2 for any other
 * row, folded on both sides like `searchOrGroup`. A search that shows only its
 * best few rows sorts on this, so a pasted identifier comes first.
 */
export const searchRankSql = (
  expr: string,
  exactPlaceholder: string,
  prefixPlaceholder: string,
): string =>
  `CASE WHEN UPPER(${expr}) = UPPER(${exactPlaceholder}) THEN 0 WHEN UPPER(${expr}) LIKE UPPER(${prefixPlaceholder}) THEN 1 ELSE 2 END`;
